import _ from 'lodash'
import $ from 'jquery'
import i18n from 'scoreboard/components/i18n'
import assignLinkGroups from 'scoreboard/components/assign-link-groups'
import ReportColumnChooser from './report-column-chooser'
import EditStackPreview from './edit-stack-preview'
import Tooltip from 'scoreboard/components/tooltip'
import { TRANSIENT_COLUMN_CLASS } from 'scoreboard/reports/common/table/header-cell'
import * as reportFormHelper from 'scoreboard/reports/common/report-form-helper'
import BaseReportEditForm from 'scoreboard/reports/common/base-report-form'
import template from './report-form.hbs'
import appDisplay from 'scoreboard/components/app-display'
import ColumnDataSourceForm from './column-data-source-form'
import MultiColumnTableStack from 'scoreboard/reports/common/table/multi-column-table-stack'
import layoutManager from 'scoreboard/components/layout-manager'
import * as reportWarnings from './report-warnings'
import EditRepeatByPreview from './edit-repeat-by-preview'
import constants from 'scoreboard/components/constants'
import icon from 'scoreboard/components/icon'

//adjusts the index used for the drop to account for the position of where the
//drop happened due dnd drop targets split into zones
function adjustDropIndex(sourceDragIndex, destDropIndex, dropZoneLeftOrTop) {
	if (destDropIndex > sourceDragIndex && dropZoneLeftOrTop) {
		return --destDropIndex
	} else if (destDropIndex < sourceDragIndex && !dropZoneLeftOrTop) {
		return ++destDropIndex
	}
	return destDropIndex
}

const BaseStandardReportEditForm = BaseReportEditForm.extend({

	className: 'table-hack-row display-pane-body report',

	events: {
		'click .add-report-footer-container, .add-report-header-container' : 'addCanvas'
	},

	/**
	 * @param options
	 * @param {Boolean} [options.embedded] = false
	 */
	initialize (options = {}) {
		BaseReportEditForm.prototype.initialize.apply(this, arguments)
		reportFormHelper.listenToChanges(this)
		this.options = options
		let TableModel = this._reportTableModel()
		this.tableModel = new TableModel()

		//ensure or groupings assigned
		assignLinkGroups(_.flatMap(this.model.get('filters')))

		//listen to any model fetches and update the view accordingly
		//this is necessary to properly update the view otherwise you get weird
		//problems like #41836 and #41837
		this.listenTo(this.model, 'request', (model, promise) => {
			//stop listening to the model when we're getting new data since we're
			//going to re-render the whole view and rebuild the table, otherwise
			//it rebuilds the table excessively from the listeners
			this.tableModel.stopListening(this.model, 'change:structure')

			promise.always(() => {
				this.render()	//re-render the view after update
			})
		})
	},

	render () {
		let templateCtx = _.extend({
			banner: this.banner(),
			warnings: this.model.warnings()
		}, this.model.attributes)

		this.$el.html(template(templateCtx))

		this._initLabelField()
		this.renderReport()

		this.$('.report-title').toggleClass('is-not-displayed', !!this.options.embedded)
		this.model.isNew() && this.$('[name=label]').select()

		//clean up left over transients if the tooltip was removed
		if (!this.reportColumnChooserTooltip) {
			this._removeTransientColumns()
		}

		return this
	},

	banner: function() {
		let bannerMessageCode = !this.options.embedded ?
			'report.designer.editInfoMessage' :
			'report.designer.dialogEditInfoMessage'
		return i18n.get(bannerMessageCode, i18n.get(this.recordTypeCode))
	},

	addCanvas(e) {
		let tar = $(e.target)
		let parentClass = tar.hasClass('.add-report-footer-container') ? '.report-footer' :'.report-header'
		//TODO: add canvas
		tar.remove()
	},

	filterView: function(el, model) {
		throw new Error('Subviews must implement this')
	},

	_updateResultMetadata: function() {
		if (this._metadataPromise) {
			return 	//if we're already waiting don't need to do anything futher
		}
		this._metadataPromise = this.model.resultsMetadata().then(meta => {
			delete this._metadataPromise
			// comment out for now due to #43148
			// if (this.currentFilters) {
			// 	this.currentFilters.renderShowingXRecords(
			// 		//maxResults is an adapter for datasets
			// 		_.extend({maxResults: meta.totalCount}, meta))
			// }
			reportWarnings.show(this.$el, this.model, meta)
		})
	},

	renderReport () {
		let {tableModel, model} = this
		this.currentFilters && this.currentFilters.remove()
		this.currentFilters = this.filterView(this.$('.filters'), model).render()
		this._updateResultMetadata()

		//#40170 ensure we remove the transient column before the filter change
		//is processed so that the fetch from the change in the filter does not
		//include the transient column
		this.currentFilters.on('deleteFilter', () => {
			_.invoke(this.reportColumnChooserTooltip, 'remove')
		})

		//#45632 if there are no columns (only for datasets) then notify with message
		if (this.model.hasEmptyDataset()) {
			let dataset = this.model.get('dataset')
			let url = 'datasets/' + dataset.id
			let label = _.escape(dataset.get('label'))
			let link = `<a href="${url}" class="sp-navigate text-link is-inlined">${label}</a>`
			let blankDatasetMessage =
				`<div class="empty-state-text">
					${i18n.get('report.designer.emptyDataset', link)}
				</div>`
			this.$('.editable-report-table').replaceWith(blankDatasetMessage)
			// Because this tab has content on top (i.e. banners, panels) that can
			// vary in height depending on wrapping, we need to do some calculations
			// to see how tall the empty-state container needs to be to fill the
			// remaining space on screen. The code below this comment handles that.
			let emptyStateRow = this.$('.add-report-header-container').next('.row')
			let emptyTopPosition = emptyStateRow[0].getBoundingClientRect().y
			let scrollContainer = this.$('.scroll-container')
			let containerHeight = scrollContainer.height()
			emptyStateRow
				.height(containerHeight - emptyTopPosition - constants.STANDARD_MARGIN*2)
			scrollContainer.addClass('empty-state no-object')
			emptyStateRow.find('.grid-w-12')
				.addClass('empty-state-message').prepend(icon('dataset'))
			return
		}

		let tableNode = this.$('.table')[0]
		this._table = tableModel.build(model, tableNode)
		this._table.on('move', this._onMoveItems.bind(this))
		this._table.on('add', this._addStack.bind(this))
		this._table.on('edit', this._editStack.bind(this))

		//if dnd is started close any open tooltip
		this._table.on('drag', () => {
			this._clearTooltips()
		})

		this._table.on('select', (sourceCell, selected) => {
			if (!sourceCell.transient) {
				//if the stack or the table is not editable, don't do anything
				if (!sourceCell.editable || !this.tableModel.isEditable()) { return }
				// remove any previous new transient or unfinished edits
				this._clearTooltips()
			}
		})

	},

	/**
	 * "move" event listener that handles rearranging of items in the table
	 *
	 * @param {HeaderCell} dragCell The cell initiating the drag
	 * @param {HeaderCell} dropCell The cell the drop is targeting
	 * @param {Object} dropZone contains positional information on the drop
	 */
	_onMoveItems: function(dragCell, dropCell, dropZone) {
		if (dragCell === dropCell) { return }

		let dragTableStack = dragCell.tableStack
		let dropTableStack = dropCell.tableStack
		let dragStack = dragCell.stack
		let dropStack = dropCell.stack

		if (dropTableStack === dragTableStack && dragStack.isRepeatByStack()) {
			//move withing a repeat by stack
			this._reorderRepeatByStack(dragCell, dropCell, dropZone)
		} else if (dropStack.isRepeatByStack() && dragStack.isRepeatByStack()) {
			if (dragCell.inBreakdownRow()) {
				//#43856 if we're dragging a breakdown to another repeat by, then
				//we're reposition the stack the drag is happening from
				this._reorderStack(dragCell, dropCell, dropZone)
			} else {
				//move between 2 repeat by stacks
				this._moveItemBetweenRepeatBy(dragCell, dropCell, dropZone)
			}
		} else if (dropStack.isRepeatByStack() && !dragStack.isRepeatByStack()) {
			if (dropCell.inBreakdownRow()) {
				//#41369 if it's breakdown row we're moving around the repeat by
				this._reorderStack(dragCell, dropCell, dropZone)
			} else {
				//move into of a repeat by stack
				this._moveItemIntoRepeatBy(dragCell, dropCell, dropZone)
			}
		} else if (!dropStack.isRepeatByStack() && dragStack.isRepeatByStack()) {
			if (dragCell.inBreakdownRow()) {
				//#43856 reposition the repeat by stack
				this._reorderStack(dragCell, dropCell, dropZone)
			} else {
				//move out a repeat by stack
				this._moveItemOutOfRepeatBy(dragCell, dropCell, dropZone)
			}
		} else {
			//reorder a non repeat by stack
			this._reorderStack(dragCell, dropCell, dropZone)
		}
	},

	_reorderRepeatByStack: function(dragCell, dropCell, dropZone) {
		let stackToRearrange = dragCell.tableStack.stack
		if (dropCell.isSameRepeatBy(dragCell)) {
			if (dropCell.isSameLevel(dragCell)) {
				//TODO: this only orders fields, because we're only
				//currently breaking down by time which cannot be reorder
				//so this will to account for other breakdown types
				let destCellIndex = adjustDropIndex(dragCell.orderIndex,
					dropCell.orderIndex, dropZone.left)
				this.model.reorderBreakdownStacks(stackToRearrange,
					dragCell.orderIndex, destCellIndex)
			} else {
				let newLevel = adjustDropIndex(dragCell.rowIndex(),
					dropCell.rowIndex(), dropZone.top)
				this.model.reorderRepeatByHeaderOrder(stackToRearrange,
					dragCell.rowIndex(), newLevel)
			}
		}
	},

	_moveItemBetweenRepeatBy: function(dragCell, dropCell, dropZone) {
		let dragColumn = dragCell.column
		let dropColumn = dropCell.column
		let dragStack = dragCell.stack
		let dropStack = dropCell.stack

		if (dragColumn && dropColumn) {
			let { orderIndex } = dropZone.headerCell
			//moving a column in one repeat by, to a column row in another repeat by
			dragStack.columns().remove(dragColumn)
			dropStack.columns().add(dragColumn, {at: orderIndex})
		}
	},

	_moveItemIntoRepeatBy: function(dragCell, dropCell, dropZone) {
		let { orderIndex } = dropZone.headerCell
		//when moving a simple stack from outside a repeat-by stack to inside
		//we'll remove the column in the old stack and insert it into
		//the repeat-by stack
		let columnToMove = dragCell.stack.columns().pop()	//should only be one
		let repeatByStack = dropCell.stack
		this.model.addColumnToRepeatBy(orderIndex, repeatByStack, columnToMove)
	},

	_moveItemOutOfRepeatBy: function(dragCell, dropCell, dropZone) {
		let addIndex = dropCell.stack.index
		let repeatByStack = dragCell.stack
		if (dragCell.column) {
			//if this is a column dragged out, remove from the repeat by and
			//add it as a new stack
			repeatByStack.columns().remove(dragCell.column)
			this.model.addStack(addIndex, dragCell.column)
		} else {
			//else we're dragging the breakdown, which rearranges the position of
			//the entire stack #43856
			this._reorderStack(dragCell, dropCell, dropZone)
		}
	},

	_reorderStack: function(dragCell, dropCell, dropZone) {
		let dragIndex = dragCell.stack.index
		let dropIndex = dropCell.stack.index
		let destIndex = adjustDropIndex(dragIndex, dropIndex, dropZone.left)
		this.model.reorderStack(dragIndex, destIndex)
	},

	_addStack(headerCell, el) {
		let { model } = this

		let addIndex = headerCell.addIndex(el)
		let stackWithIndex = this.model.stacks().findStackByColumnAttribute({transient: true})
		if (!_.isEmpty(stackWithIndex)) {
			let stackWithTransient = stackWithIndex.model
			let isAddAndTransientInSameRepeatBy = stackWithTransient == headerCell.stack
			let isAddAndTransientBasicStacks = !stackWithTransient.isRepeatByStack()
				&& !headerCell.stack.isRepeatByStack()
			//if we're adding/removing within the same repeat by or they both are
			//basic stacks, then we must subtract 1 from the add index to account for
			//the transient being removed due to the add #40142
			if ((isAddAndTransientInSameRepeatBy || isAddAndTransientBasicStacks) && stackWithIndex.index < addIndex) {
				addIndex--
			}
		}

		//remove any previous tooltips
		this._clearTooltips()

		let {tableStack, stack} = headerCell
		let optionData = this.model.columnOptionsList(stack, {forColumn: true})
		//the default selection is the first option in the list, #39635 but we're
		//using a temporary "New Column" label so it's more clear
		let fieldIdOrColumnType = optionData[0].id

		let stackWithRepeatBy = null
		if (tableStack instanceof MultiColumnTableStack) {
			stackWithRepeatBy = stack
		}
		this._addNewColumnToReportModel(addIndex,
			fieldIdOrColumnType, false, stackWithRepeatBy)

		this.reportColumnChooser = new ReportColumnChooser({optionData, fieldIdOrColumnType,
			dataset: model.get('dataset')})

		let attachTo = this.$(`td.${TRANSIENT_COLUMN_CLASS}`).find(`.icon-diamond-add`)
		//add tooltip
		this.reportColumnChooserTooltip = new Tooltip({
			attachTo,
			content: this.reportColumnChooser,
			placement: 'left',
			arrow: 'center',
			extraClass: 'designer-dataset-field-chooser',
			alternatePlacements: ['below']
		}).render()

		//make stack addition permanent
		this.reportColumnChooser.on('select', fieldIdOrColumnType => {
			//update the current transient
			const updated = this._updateTransient(fieldIdOrColumnType, stackWithRepeatBy)
			if (updated) {
				const index = updated.index
				const stack = updated.stack
				//if the transient creates a new multi column stack, future adds should
				//add to that multi column stack
				if (stack.isRepeatByStack() && stackWithRepeatBy == null) {
					stackWithRepeatBy = stack
				}

				//create a new transient
				this._addNewColumnToReportModel(index+1, fieldIdOrColumnType,
					false, stackWithRepeatBy)
			} else {
				//if no transients were found to be updated, destroy the tooltip
				_.invoke(this.reportColumnChooserTooltip, 'remove')
			}
		})

		//show the full edit dialog, if user selected "new calculation options"
		this.reportColumnChooser.on('details', fieldIdOrColumnType => {
			this.stopListening(this.reportColumnChooserTooltip, 'remove')      //#40020
			this._showNewCaluclationView()
		})

		//remove transient stack if it was not made permanent and user closed the tooltip
		this.listenToOnce(this.reportColumnChooserTooltip, 'remove', () => {
			this._removeTransientColumns()
			delete this.reportColumnChooserTooltip
		})
	},

	_updateTransient: function(fieldIdOrColumnType, stackWithRepeatBy) {
		//find the index of the transient
		let currentAddIndex = this._findTransientColumnIndex(stackWithRepeatBy)
		if (!_.isNil(currentAddIndex)) {
			//update the transient to the given field/column type
			let updatedStackToFinalize = this._addNewColumnToReportModel(currentAddIndex,
				fieldIdOrColumnType, true, stackWithRepeatBy)
			updatedStackToFinalize.finalizeTransientColumn(this.model, this.tableModel)
				.then(() => {
					//#43177 if the tooltip was closed during the fetch, remove the
					//leftover transient
					if (!this.reportColumnChooserTooltip) {
						this._removeTransientColumns()
					}
				})
			return {index: currentAddIndex, stack: updatedStackToFinalize}
		}
		return null  //if no transient to update return null
	},

	_findTransientColumnIndex: function(stackWithRepeatBy) {
		if (stackWithRepeatBy && stackWithRepeatBy.isRepeatByStack()) {
			return stackWithRepeatBy.findColumnByAttribute({transient: true}, {columnIndex: true})
		} else {
			let {index} = this.model.stacks().findStackByColumnAttribute({transient: true})
			return index
		}
	},

	_removeTransientColumns: function() {
		_.invokeMap(this.model.stacks().models, 'removeTransientColumn')
	},

	_showNewCaluclationView: function() {
		let transientColumnIndex = this._findTransientColumnIndex()
		if (_.isNil(transientColumnIndex)) { return }
		let stackWithNewColumn = this.model.stacks(transientColumnIndex)

		// #40765 Brand new stack = only one column =
		// it's safe and desirable to use static index 0.
		// Note: this.columnAddIndex is completely erroneous here as it is the index
		// of the stack in the stacks collection not the column in stack.columns.
		let column =  stackWithNewColumn.columns().at(0)
		column.set('calculated', true, {silent: true})
		column.unset('equation', {silent: true})
		let view = new ColumnDataSourceForm({
			model: stackWithNewColumn,
			reportModel: this.model,
			tableModel: this.tableModel,
			column,
			heading: i18n.get('report.designer.addColumn'),
			primaryButtonText: 'Add'
		})
		view.on('done', updatedMeta => {
			_.invoke(this.reportColumnChooserTooltip, 'remove')
			//update the column
			stackWithNewColumn.columns(0).set(updatedMeta.attributes)
			//make it permanent
			stackWithNewColumn.finalizeTransientColumn(this.model, this.tableModel)
		})
		view.on('cancel', () => {
			this._removeTransientColumns()	//#40081
		})
		appDisplay.dialog(view)
	},

	/**
	 * adds a new column to the report model, which updates the data model
	 * that then triggers re-render of table
	 *
	 * returns the stack the column was added to
	 *
	 * @param {Integer|String} fieldIdOrColumnType the field id or column type
	 * of the new column
	 * @param {Boolean} replace true if replacing the current column at the index
	 * @param [{ReportStack}] stackWithRepeatBy stack with repeat by if the column
	 * being added is to be added to a existing stack with repeat by
	 * @return {ReportStack}
	 * @private
	 */
	_addNewColumnToReportModel: function(addIndex, fieldIdOrColumnType,
		replace, stackWithRepeatBy) {
		//update the data model which triggers re-render of table
		//returns the stack the column was added to
		let columnOpts =  {transient: true, replace, newBlankColumn: !replace}

		let newStack
		if (stackWithRepeatBy && stackWithRepeatBy.isRepeatByStack()) {
			newStack = this.model.addColumnToRepeatBy(addIndex,
				stackWithRepeatBy, fieldIdOrColumnType, columnOpts)
		} else {
			newStack = this.model.addStack(addIndex, fieldIdOrColumnType,
				columnOpts)
		}
		return newStack
	},

	_editStack: function(headerCell, nodes, el, e) {
		//if the stack or the table is not editable, don't do anything
		if (!headerCell.editable || !this.tableModel.isEditable()) { return }

		let {stack, tableStack} = headerCell

		//determine the tooltip to use
		if (tableStack instanceof MultiColumnTableStack &&
			headerCell.inBreakdownRow()) {
			//if the stack is a multi layout and the row is a breakdown, edit the repeatBy
			this.editPreview = this.editRepeatByPreview(headerCell)
		} else {
			//otherwise we're editing the column
			let {model, tableModel} = this
			this.editPreview = new EditStackPreview({
				headerCell, stack, model, tableModel
			})
		}

		//determine the cell to attach the tooltip to, which is the cell
		//being clicked
		let attachTo = el

		// If the stack's clicked cell is partially out of view to the left, use the
		// click event to place the tooltip, otherwise, tooltip.js will just place
		// the tooltip in the middle of the cell. If this form is within a dialog,
		// the menu wont be in the way so there's no need for left adjustment.
		let left, menuWidth = layoutManager.getMenuWidth()
		let outsideDialog = this.$(el).closest('.dialog').length == 0
		if (outsideDialog && el.getBoundingClientRect().x < menuWidth) {
			left = e.clientX
		}

		//add tooltip
		this.editStackTooltip = new Tooltip({
			attachTo,
			content: this.editPreview,
			placement: 'above',
			arrow: 'center',
			extraClass: 'has-bootslide-menu edit-stack-preview-tooltip',
			left,
			alternatePlacements: ['right', 'left']
		}).render()

		//add reference to the stack this tooltip is reference
		this.editStackTooltip.stack = tableStack

		//deselect if the preview is destroyed
		this.listenToOnce(this.editStackTooltip, 'remove', () => {
			tableStack.select(false, null, null, {silent: true})
		})
	},

	editRepeatByPreview(headerCell) {
		let {model, tableModel} = this
		return new EditRepeatByPreview({
			headerCell,
			model,
			tableModel
		})
	},

	/**
	 * Handles pre removal actions. In this case for reports stops the table
	 * model and the view itself from listening events from the report
	 * model since the view will be removed, and should no longer be concerned
	 * with the data emitting from the report model
	 */
	prepareRemove() {
		this.stopListening(this.model)
		this.tableModel.remove()
		//clear any tooltip so that transient columns are removed before the
		//fetch is done by the reports-router, not doing so will cause these
		//transients to stick around
		this._clearTooltips()
	},

	_clearTooltips: function() {
		_.invokeMap([this.reportColumnChooserTooltip, this.editStackTooltip], 'remove')
	},

	remove() {
		BaseReportEditForm.prototype.remove.apply(this, arguments)
		this._clearTooltips()
		_.invokeMap([this.currentFilters, this._table,
			this.reportColumnChooser, this.editPreview,
			this.loadingDots, this.tableModel], 'remove')
	}
})

export default BaseStandardReportEditForm
BaseStandardReportEditForm.__filename__ = 'reports/common/base-standard-report-edit-form.js'
